import React, {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useState,
} from 'react';
import { Loader } from 'components/Loader/Loader';
import ControlSortList, { SortListBundleType } from './ControlSortList';
import './SortList.scss';
import { Region } from '../../types/orderTypes';
import {
  fetchHubSortListEntriesNew,
  AllBundlesResponse,
} from '../../fetch/fetchHubSortListEntriesAll';
import _ from 'lodash';
import {
  useDeepCompareCallback,
  useDeepCompareEffect,
  useDeepCompareMemo,
} from 'use-deep-compare';
import {
  PusherEvent,
  subscribeToChannel,
  unsubscribeFromChannel,
} from '../../utils/pusher';
import { fetchSortListBundles } from '../../fetch/fetchHubSortListEntries';

const throttledDispatch = _.throttle(
  async (
    setTemporaryListOfBundlesToUpdate: Dispatch<SetStateAction<string[]>>,
    bundleIdsToUpdate: string[],
    clearIds: (bundleIds: string[]) => void
  ) => {
    setTemporaryListOfBundlesToUpdate(bundleIdsToUpdate);
    clearIds(bundleIdsToUpdate);
  },
  10000,
  { leading: false }
);

export function subscribeToPusher(
  setTemporaryListOfBundlesToUpdate: Dispatch<SetStateAction<string[]>>
): void {
  let sortListBundlesToUpdate: string[] = [];
  subscribeToChannel(
    'admin-hubscans',
    PusherEvent.HUBSORT_BUNDLE_UPDATE,
    (event: { orderId: string; bundleId: string }) => {
      const { bundleId } = event;
      const clearIds = (updatedIds: string[]) => {
        if (sortListBundlesToUpdate.length === updatedIds.length) {
          sortListBundlesToUpdate = [];
        } else {
          sortListBundlesToUpdate = sortListBundlesToUpdate.filter(
            id => !updatedIds.includes(id)
          );
        }
      };
      sortListBundlesToUpdate.push(bundleId);
      throttledDispatch(
        setTemporaryListOfBundlesToUpdate,
        sortListBundlesToUpdate,
        clearIds
      );
    }
  );
}

export default function SortList({ region }: { region: Region }): JSX.Element {
  const [loaderVisible, toggleLoader] = useState<boolean>(false);
  const [temporaryListOfOrdersToUpdate, setTemporaryListOfOrdersToUpdate] =
    useState<string[]>([]);
  const [sortListBundles, setSortListBundles] = useState<
    AllBundlesResponse | undefined
  >(undefined);

  const currentBundlesId = sortListBundles
    ? sortListBundles.incompletedBundles
        .map(bundle => bundle.id)
        .concat(sortListBundles.completedBundles.map(bundle => bundle.id))
    : [];

  const memoizedCurrentBundlesId = useDeepCompareMemo(() => {
    return currentBundlesId;
  }, [currentBundlesId]);

  const subscribeToPusherCallback = useCallback(
    () => subscribeToPusher(setTemporaryListOfOrdersToUpdate),
    []
  );

  const fetchHubSortList = useCallback(async () => {
    toggleLoader(true);
    const data = await fetchHubSortListEntriesNew(region);
    setSortListBundles(data);
  }, [region]);

  const crossExamineBundlesAndUpdate = (
    bundlesTypeToAdd: SortListBundleType,
    bundlesTypeToRemove: SortListBundleType,
    bundlesList: AllBundlesResponse | undefined,
    updatedBundles: AllBundlesResponse | undefined
  ): AllBundlesResponse | undefined => {
    if (bundlesList && updatedBundles) {
      //this method is to examine whether the bundle which was in 'incompleted' should be
      // removed from this object and should be moved/add to completed and vice versa after the
      // silent update
      //copying to not mutate state
      const copyOfBundlesList = { ...bundlesList };
      //looping through arrays of incompleted or completed based on bundlesTypeToAdd or bundlesTypeToRemove
      updatedBundles[bundlesTypeToAdd].forEach(updatedBundle => {
        let bundleToAdd = true;
        copyOfBundlesList[bundlesTypeToAdd] = copyOfBundlesList[
          bundlesTypeToAdd
        ].map(bundle => {
          //if id matching, updating structure which i received from silent update
          if (bundle.id === updatedBundle.id) {
            bundleToAdd = false;
            return updatedBundle;
          } else {
            //if not i am just adding the same bundle since it should be there
            return bundle;
          }
        });
        //if flag didn't change I have to add bundle which i receive from silet update to the
        // respective object 'incompleted' or 'completed' based on bundlesTypeToAdd
        if (bundleToAdd) {
          copyOfBundlesList[bundlesTypeToAdd].push(updatedBundle);
        }
        copyOfBundlesList[bundlesTypeToRemove] = copyOfBundlesList[
          bundlesTypeToRemove
        ].filter(bundle => bundle.id !== updatedBundle.id);
      });
      return copyOfBundlesList;
    }
    return undefined;
  };

  const updateCurrentSortListBundles = useCallback(
    (updatedBundles: AllBundlesResponse | undefined) => {
      const firstIterationUpdatedBundlesList = crossExamineBundlesAndUpdate(
        'incompletedBundles',
        'completedBundles',
        sortListBundles,
        updatedBundles
      );
      return crossExamineBundlesAndUpdate(
        'completedBundles',
        'incompletedBundles',
        firstIterationUpdatedBundlesList,
        updatedBundles
      );
    },
    [sortListBundles]
  );

  const updateHubSortListEntries = useCallback(
    async (bundlesId: string[]) =>
      setSortListBundles(
        updateCurrentSortListBundles(await fetchSortListBundles(bundlesId))
      ),
    [updateCurrentSortListBundles]
  );

  const performSearchAndUpdateBundles = useDeepCompareCallback(async () => {
    const bundlesToUpdate: string[] = [];
    setTemporaryListOfOrdersToUpdate([]);
    temporaryListOfOrdersToUpdate.forEach(bundleId => {
      if (memoizedCurrentBundlesId.includes(+bundleId)) {
        console.log('id to update', bundleId);
        bundlesToUpdate.push(bundleId);
      }
    });
    if (bundlesToUpdate.length) {
      await updateHubSortListEntries(bundlesToUpdate);
    }
  }, [
    memoizedCurrentBundlesId,
    temporaryListOfOrdersToUpdate,
    updateCurrentSortListBundles,
  ]);

  useEffect(() => {
    fetchHubSortList()
      .then(() => {
        console.log('Sort list fetched');
        toggleLoader(false);
      })
      .catch(error => console.log(error));
  }, [fetchHubSortList]);

  useDeepCompareEffect(() => {
    if (temporaryListOfOrdersToUpdate.length) {
      console.log(temporaryListOfOrdersToUpdate);
      performSearchAndUpdateBundles()
        .then(() => console.log('Sort list updated'))
        .catch(error => console.log(error));
    }
  }, [performSearchAndUpdateBundles, temporaryListOfOrdersToUpdate]);

  useEffect(() => {
    subscribeToPusherCallback();
    return () => {
      unsubscribeFromChannel('hubsort-bundle-update');
    };
  }, [fetchHubSortList, subscribeToPusherCallback]);

  let mainContent;
  if (loaderVisible) {
    mainContent = (
      <div className="loader-wrapper">
        <Loader type="spinner" size="xxl" />
      </div>
    );
  } else if (sortListBundles) {
    mainContent = (
      <div className="sort-list">
        <ControlSortList
          updateHubSortListEntries={updateHubSortListEntries}
          region={region}
          sortListBundles={sortListBundles}
        />
      </div>
    );
  } else {
    mainContent = null;
  }
  return <>{mainContent}</>;
}
